import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { auth, fmtAmt, m2ToPy, lbl, inp, sel, card } from '../RealTradePage';

// ─────────────────────────────────────────────
// 단독·다가구 탭 - /api/real-trades/sh
//
//  [공동주택 탭과 분리한 이유]
//   · 지번이 마스킹(6**)되어 단지 식별이 불가능하다
//   · 전용면적·층이 없고 대지면적/연면적으로 거래된다
//   → 개별 단지 추적이 아닌 "동 단위 대지 평당가 시세" 파악이 목적
// ─────────────────────────────────────────────

const HOUSE_TYPES = [
  { v: '',      t: '전체' },
  { v: '단독',  t: '단독주택' },
  { v: '다가구', t: '다가구' },
  { v: '다중',  t: '다중주택' },
];

const SORTS = [
  { v: 'date_desc',   t: '최근 거래순' },
  { v: 'land_desc',   t: '대지평당가 높은순' },
  { v: 'land_asc',    t: '대지평당가 낮은순' },
  { v: 'amount_desc', t: '금액 높은순' },
  { v: 'plot_desc',   t: '대지 넓은순' },
];

export default function ShTrade({ lawdCd, zone, from, to, regions }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [umd, setUmd]         = useState('');
  const [hType, setHType]     = useState('');
  const [sort, setSort]       = useState('date_desc');
  const [plotMin, setPlotMin] = useState('');
  const [plotMax, setPlotMax] = useState('');

  const load = useCallback(async () => {
    if (!lawdCd && !zone) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ from, to, sort, size: 200 });
      if (lawdCd) q.set('lawd_cd', lawdCd);
      else if (zone) q.set('zone', zone);
      if (umd)     q.set('umd_nm', umd);
      if (hType)   q.set('house_type', hType);
      if (plotMin) q.set('plot_min', plotMin);
      if (plotMax) q.set('plot_max', plotMax);

      const res = await axios.get(`/api/real-trades/sh?${q}`, auth());
      setData(res.data);
    } catch (e) {
      alert('조회 실패: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [lawdCd, zone, from, to, sort, umd, hType, plotMin, plotMax]);

  useEffect(() => { load(); }, [load]);

  const sggName = zone || regions.find(r => r.lawd_cd === lawdCd)?.sgg_nm || '';

  const monthData = (data?.by_month || []).map(m => ({
    ym: `${m.deal_ym.slice(2,4)}.${m.deal_ym.slice(4,6)}`,
    거래량: m.cnt,
    대지평당가: m.avg_land_unit,
  }));

  const s = data?.summary;

  return (
    <div>
      {/* ── 필터 ── */}
      <div style={{ ...card, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={lbl}>주택유형</label>
        <select value={hType} onChange={e => setHType(e.target.value)} style={sel}>
          {HOUSE_TYPES.map(h => <option key={h.v} value={h.v}>{h.t}</option>)}
        </select>

        <label style={lbl}>법정동</label>
        <select value={umd} onChange={e => setUmd(e.target.value)} style={sel}>
          <option value="">전체</option>
          {data?.by_umd?.map(u => (
            <option key={u.umd_nm} value={u.umd_nm}>{u.umd_nm} ({u.cnt})</option>
          ))}
        </select>

        <label style={lbl}>대지면적</label>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <input value={plotMin} onChange={e => setPlotMin(e.target.value.replace(/[^0-9.]/g,''))}
                 placeholder="이상" style={{ ...inp, width: 70 }} />
          <span style={pyHint}>{plotMin && `${m2ToPy(plotMin)}평`}</span>
        </div>
        <span style={{ color:'#94a3b8', alignSelf:'flex-start', marginTop:8 }}>~</span>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <input value={plotMax} onChange={e => setPlotMax(e.target.value.replace(/[^0-9.]/g,''))}
                 placeholder="이하" style={{ ...inp, width: 70 }} />
          <span style={pyHint}>{plotMax && `${m2ToPy(plotMax)}평`}</span>
        </div>
        <span style={{ fontSize:12, color:'#64748b', alignSelf:'flex-start', marginTop:8 }}>㎡</span>

        <label style={{ ...lbl, marginLeft: 8 }}>정렬</label>
        <select value={sort} onChange={e => setSort(e.target.value)} style={sel}>
          {SORTS.map(x => <option key={x.v} value={x.v}>{x.t}</option>)}
        </select>
      </div>

      {loading && <div style={{ padding: 20, color: '#3b82f6' }}>조회 중…</div>}

      {s && s.cnt > 0 && (
        <>
          {/* ── 요약 ── */}
          <div style={card}>
            <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>
              {sggName} 단독·다가구 시세
              <span style={{ fontSize:12, fontWeight:400, color:'#64748b', marginLeft:8 }}>
                {from} ~ {to}
              </span>
            </h3>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Stat label="거래건수" value={`${s.cnt}건`} />
              <Stat label="평균 대지 평당가" value={`${s.avg_land_unit?.toLocaleString()}만`} hl />
              <Stat label="평균 연면적 평당가" value={`${s.avg_floor_unit?.toLocaleString()}만`} />
              <Stat label="평균 거래가" value={fmtAmt(s.avg_amt)} />
              <Stat label="평균 대지" value={`${m2ToPy(s.avg_plot)}평`} />
              <Stat label="최고 거래가" value={fmtAmt(s.max_amt)} />
            </div>
            <p style={{ margin:'10px 0 0', fontSize:11, color:'#94a3b8', lineHeight:1.6 }}>
              ※ 단독주택 시세는 <b>대지 평당가</b>로 보는 것이 실무 관행입니다.<br/>
              ※ {data.note}
            </p>
          </div>

          {/* ── 유형별 ── */}
          {data.by_type?.length > 1 && (
            <div style={{ ...card, display:'flex', gap:16, flexWrap:'wrap' }}>
              {data.by_type.map(t => (
                <div key={t.house_type} style={{ flex:'1 1 160px', minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#475569' }}>
                    {t.house_type}
                  </div>
                  <div style={{ fontSize:12, color:'#64748b', marginTop:4 }}>
                    {t.cnt}건 · 평균 {fmtAmt(t.avg_amt)}<br/>
                    대지 평당 <b style={{ color:'#1e40af' }}>
                      {t.avg_land_unit?.toLocaleString()}만</b>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── 월별 추이 ── */}
          <div style={card}>
            <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>월별 거래량 · 대지 평당가</h4>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthData} margin={{ top:10, right:20, bottom:5, left:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="ym" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="L" tick={{ fontSize: 11 }} width={40} />
                <YAxis yAxisId="R" orientation="right" tick={{ fontSize: 11 }} width={55}
                       tickFormatter={v => (v/1000).toFixed(1)+'천'} />
                <Tooltip formatter={(v,n) =>
                  n === '거래량' ? [v+'건', n] : [v?.toLocaleString()+'만원/평', n]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line yAxisId="L" type="monotone" dataKey="거래량"
                      stroke="#94a3b8" strokeWidth={2} dot={{ r:3 }} />
                <Line yAxisId="R" type="monotone" dataKey="대지평당가"
                      stroke="#f59e0b" strokeWidth={2} dot={{ r:3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* ── 법정동별 시세 ── */}
          <div style={card}>
            <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>법정동별 대지 평당가</h4>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f1f5f9' }}>
                  {['법정동','거래','평균 대지','대지 평당가','최고','최저','평균 거래가']
                    .map(h => <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.by_umd.map(u => (
                  <tr key={u.umd_nm} style={{ borderTop:'1px solid #f1f5f9' }}>
                    <td style={td}>{u.umd_nm}</td>
                    <td style={tdR}>{u.cnt}건</td>
                    <td style={tdR}>{m2ToPy(u.avg_plot)}평</td>
                    <td style={{ ...tdR, fontWeight:700, color:'#1e40af' }}>
                      {u.avg_land_unit?.toLocaleString()}</td>
                    <td style={{ ...tdR, color:'#dc2626' }}>
                      {u.max_land_unit?.toLocaleString()}</td>
                    <td style={{ ...tdR, color:'#2563eb' }}>
                      {u.min_land_unit?.toLocaleString()}</td>
                    <td style={tdR}>{fmtAmt(u.avg_amt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── 거래 목록 ── */}
          <div style={{ marginBottom:8, fontSize:13, color:'#475569' }}>
            거래 내역 <b>{data.rows.length}</b>건
          </div>
          <div style={{ overflowX:'auto', border:'1px solid #e2e8f0', borderRadius:8 }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f1f5f9' }}>
                  {['거래일','법정동','지번','유형','대지','연면적','준공',
                    '거래금액','대지평당','연면적평당','거래구분']
                    .map(h => <th key={h} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r,i) => (
                  <tr key={i} style={{ borderTop:'1px solid #f1f5f9' }}>
                    <td style={td}>{r.deal_date}</td>
                    <td style={td}>{r.umd_nm}</td>
                    <td style={{ ...td, color:'#94a3b8' }}>{r.jibun}</td>
                    <td style={td}>{r.house_type}</td>
                    <td style={tdR}>{m2ToPy(r.plottage_ar)}평</td>
                    <td style={tdR}>{r.total_floor_ar ? m2ToPy(r.total_floor_ar)+'평' : '-'}</td>
                    <td style={tdR}>{r.build_year || '-'}</td>
                    <td style={{ ...tdR, fontWeight:600, color:'#1e40af' }}>
                      {fmtAmt(r.deal_amount)}</td>
                    <td style={{ ...tdR, fontWeight:600 }}>
                      {r.land_unit_price?.toLocaleString()}</td>
                    <td style={tdR}>{r.floor_unit_price?.toLocaleString() || '-'}</td>
                    <td style={td}>{r.dealing_gbn || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {s && s.cnt === 0 && !loading && (
        <div style={{ ...card, textAlign:'center', color:'#94a3b8', padding:30 }}>
          해당 조건의 단독·다가구 거래가 없습니다<br/>
          <span style={{ fontSize:12 }}>
            (단독·다가구는 아직 강남구 1개월분만 수집되어 있습니다)
          </span>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hl }) {
  return (
    <div style={{ flex:'1 1 130px', minWidth:0, padding:'10px 12px',
                  background: hl ? '#fffbeb' : '#f8fafc', borderRadius:6 }}>
      <div style={{ fontSize:11, color:'#64748b', marginBottom:3 }}>{label}</div>
      <div style={{ fontSize:16, fontWeight:700,
                    color: hl ? '#b45309' : '#1e293b' }}>{value}</div>
    </div>
  );
}

const th  = { padding:'9px 8px', textAlign:'left', fontWeight:600,
              color:'#475569', whiteSpace:'nowrap' };
const td  = { padding:'8px', whiteSpace:'nowrap' };
const tdR = { ...td, textAlign:'right' };
const pyHint = { fontSize:10, color:'#3b82f6', textAlign:'right',
                 paddingRight:2, height:12 };